/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import { t } from "i18next";


interface IncubationParametersProps {
  incubationData: any;
  setIncubationData: React.Dispatch<React.SetStateAction<any>>;
  incubators: any[];
  durationInfo: {
    value: string;
    unit: string;
    isValid: boolean;
  };
  errors: any;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const IncubationParameters: React.FC<IncubationParametersProps> = ({
  incubationData,
  setIncubationData,
  incubators,
  durationInfo,  
  errors,
  setErrors
}) => {
  return (
    <>
    {/* Section 1: Incubation Parameters */}
          <section className="bg-white rounded-[1rem] border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500 delay-75">
            <div className="px-6 py-2 border-b border-slate-100 bg-slate-50/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-sm font-black shadow-sm">02</div>
                <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-[0.2em]">{t("gptTest.gptTestDetails.incubationDetails")}</h3>
              </div>
              {/* <div className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-5 py-2 flex items-center gap-3"> */}
                 {/* <span className="material-symbols-outlined text-lg font-black">info</span> */}
                 {/* <span className="text-[11px] font-black uppercase tracking-widest">Protocol Ref: SOP-QC-72v4</span> */}
              {/* </div> */}
            </div>
            <div className="p-6 pt-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                 <div className="lg:col-span-8 space-y-4">
                   <div className="grid grid-cols-2 gap-12">
                     <div className="space-y-3">
                       <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.startDateTime")} <span className="text-red-500">*</span></label>
                       <input
                        type="datetime-local"
                        value={incubationData.startDate}
                        max={new Date().toISOString().slice(0, 16)}
                        onChange={(e) => {
                          setIncubationData({
                            ...incubationData,
                            startDate: e.target.value,
                          });
                          setErrors((prev: any) => ({
                            ...prev,
                            startDate: "",
                            endDate: "",
                          }));
                        }}
                        className="w-full px-6 py-2 bg-slate-50/50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:bg-white focus:border-[#FF7043] focus:ring-8 focus:ring-orange-500/5 transition-all" 
                       />
                       {errors.startDate && <p className="text-[11px] text-red-500">{errors.startDate}</p>}
                     </div>
                     
                     <div className="space-y-3">
                       <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.endDateTime")} <span className="text-red-500">*</span></label>
                       <input 
                        type="datetime-local" 
                        value={incubationData.endDate}
                        min={incubationData.startDate}
                        max={new Date().toISOString().slice(0, 16)}
                        onChange={(e) => {
                          setIncubationData({ ...incubationData, endDate: e.target.value });
                          setErrors((prev: any) => ({ ...prev, endDate: "" }));
                        }}
                        className="w-full px-6 py-2 bg-slate-50/50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:bg-white focus:border-[#FF7043] focus:ring-8 focus:ring-orange-500/5 transition-all" 
                       />
                        {errors.endDate && <p className="text-[11px] text-red-500">{errors.endDate}</p>}
                     </div>
                   </div>

                   <div className="grid grid-cols-2 gap-12">
                      <div className="space-y-3">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.temperature")} <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <input 
                            type="number" 
                            step="0.1"
                            value={incubationData.temperature}
                            onChange={(e) => {
                              setIncubationData({ ...incubationData, temperature: e.target.value });
                              setErrors((prev: any) => ({ ...prev, temperature: "" }));
                            }}
                            className="w-full px-6 py-2 bg-slate-50/50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:bg-white focus:border-[#FF7043] transition-all" 
                            placeholder="eg. 32.5"
                          />
                          <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xs font-black text-slate-300 tracking-widest">°C</span>
                          {errors.temperature && <p className="text-[11px] text-red-500">{errors.temperature}</p>}
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.incubatorID")} <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <select 
                            value={incubationData.incubatorId}
                            onChange={(e) => {
                              setIncubationData({ ...incubationData, incubatorId: e.target.value });
                              setErrors((prev: any) => ({ ...prev, incubatorId: "" }));
                            }}
                            className="w-full px-6 py-2 bg-slate-50/50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:bg-white focus:border-[#FF7043] transition-all appearance-none cursor-pointer"
                          >
                            <option value="">Select unit</option>
                            {incubators.map((item: any) => (
                              <option key={item.id} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                          </select>
                          {errors.incubatorId && <p className="text-[11px] text-red-500">{errors.incubatorId}</p>}
                          <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">{t("gptTest.icons.expand_more")}</span>
                        </div>
                      </div>
                   </div>
                 </div>

                 <div className="lg:col-span-4 flex flex-col">
                    <div className="flex-1 bg-slate-50 border border-slate-100 rounded-[3rem] p-10 flex flex-col items-center justify-center text-center gap-2 relative overflow-hidden group hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500">
                      <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                        <span className="material-symbols-outlined text-[25px]">{t("gptTest.icons.schedule")}</span>
                      </div>
                      <p className="text-[12px]  text-slate-400 font-black uppercase tracking-[0.3em] relative z-10">{t("gptTest.gptTestDetails.calculatedCycle")}</p>
                      <div className="flex items-baseline gap-3 relative z-10">
                        <span className={`text-4xl font-black tracking-tighter ${durationInfo.value !== '--' ? 'text-[#2D3E50]' : 'text-slate-300'}`}>
                          {durationInfo.value}
                        </span>
                        <span className="text-lg text-slate-400 uppercase tracking-widest">{durationInfo.unit}</span>
                      </div>
                      <div className={`mt-4 px-8 py-2 rounded-full flex items-center gap-2 border-2 relative z-10 transition-all duration-500 ${durationInfo.isValid ? 'bg-emerald-50 text-emerald-600 border-emerald-100 shadow-sm' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
                        <span className="material-symbols-outlined text-lg font-black">
                          {durationInfo.isValid ? 'verified' : 'pending_actions'}
                        </span>
                        <span className="text-[11px] font-black uppercase tracking-[0.1em]">
                          {durationInfo.isValid ? 'SOP Window Valid' : 'Awaiting Data Completion'}
                        </span>
                      </div>
                    </div>
                 </div>
              </div>
            </div>
          </section>
    </>
    );
};

export default IncubationParameters;